<template>
    <vx-expandable-card>
        <template v-slot:header>Group Status</template>

        <template v-if="!currentStatus" v-slot:content>
            <page-loading class="py-8"></page-loading>
        </template>

        <template v-else v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <template v-if="scheduledChange">
                    <vx-alert data-test="scheduled-status-callout" :closeable="false">
                        <div class="text-sm">
                            <div class="font-semibold">Status Change Scheduled</div>
                            <div>
                                Status will change from
                                <strong class="capitalize">{{ currentStatus }}</strong>
                                to
                                <strong class="capitalize">{{ scheduledChange.target_status }}</strong>
                                on {{ scheduledDateFormatted }}
                            </div>
                        </div>
                    </vx-alert>

                    <vx-button
                        v-if="props.isEditable"
                        size="lg"
                        block
                        :disabled="cancelling"
                        :loading="cancelling"
                        @click="cancelScheduled"
                        data-test="scheduled-cancel-button"
                        color="primary"
                    >
                        <font-awesome-icon widthAuto :icon="faXmark" />
                        Cancel Scheduled Change
                    </vx-button>
                </template>

                <template v-else>
                    <group-status-radios
                        v-model="selected"
                        :currentStatus="currentStatus!"
                        :isBillable="Boolean(group?.is_billable)"
                        :disabled="!props.isEditable"
                        aria-label="Group status"
                    />

                    <div v-if="props.isEditable" class="flex gap-4 items-center">
                        <vx-button
                            size="lg"
                            :disabled="!canSchedule"
                            :loading="submitting"
                            @click.prevent="scheduleModalVisible = true"
                            data-test="group-status-schedule"
                            color="muted"
                        >
                            <font-awesome-icon widthAuto :icon="faCalendar" />
                            Schedule for Later
                        </vx-button>
                        <vx-button
                            size="lg"
                            class="grow"
                            :disabled="!canSubmit"
                            :loading="submitting"
                            @click.prevent="onPrimaryClick"
                            data-test="group-status-submit"
                        >
                            Update Now
                        </vx-button>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>

    <schedule-group-status-update-modal
        v-if="scheduleModalVisible && selected !== GROUP_STATUS_DRAFT"
        v-model:visible="scheduleModalVisible"
        :targetStatus="selected"
        :loading="submitting"
        @schedule="submitScheduled"
    />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendar, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupStatusRadios from '~/components/kiosk/kiosk-groups/GroupStatusRadios.vue';
import ScheduleGroupStatusUpdateModal from './ScheduleGroupStatusUpdateModal.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminGroupsClient } from '~/services';
import type { ScheduledStatusChange } from '~/services/adminGroupsClient.types';
import { GROUP_STATUS_CLOSED, GROUP_STATUS_DRAFT, type GroupStatus } from '~/constants/groupStatuses';
import dayjs from '~/utils/dayjs';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';

const props = withDefaults(
    defineProps<{
        teamId: number;
        groupId: string;
        isEditable?: boolean;
    }>(),
    {
        isEditable: true,
    }
);

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const { group, refreshGroup } = useGroupDetails();

const selected = ref<GroupStatus>(GROUP_STATUS_DRAFT);
const submitting = ref(false);
const cancelling = ref(false);
const scheduleModalVisible = ref(false);
const scheduledChange = ref<ScheduledStatusChange | null>(null);

const currentStatus = computed<GroupStatus | undefined>(() => group.value?.status);

watch(
    currentStatus,
    (value) => {
        if (value) {
            selected.value = value;
        }
    },
    { immediate: true }
);

const loadScheduledChange = async () => {
    try {
        const response = await adminGroupsClient.getScheduledStatusUpdate(props.teamId, props.groupId);
        scheduledChange.value = response.data;
    } catch (e) {
        if ((e as { response?: { status?: number } })?.response?.status === HTTP_NOT_FOUND) {
            scheduledChange.value = null;
            return;
        }
        catchError(e);
    }
};

const refresh = async () => {
    await Promise.all([refreshGroup(props.teamId, props.groupId), loadScheduledChange()]);
};

onMounted(() => {
    loadScheduledChange();
});

const scheduledDateFormatted = computed(() => {
    if (!scheduledChange.value?.scheduled_date) {
        return '';
    }
    return dayjs(scheduledChange.value.scheduled_date, 'YYYY-MM-DD').format('M/D/YY');
});
const canSubmit = computed(
    () => props.isEditable && !!currentStatus.value && selected.value !== currentStatus.value && !submitting.value
);

const canSchedule = computed(() => canSubmit.value && selected.value !== GROUP_STATUS_DRAFT);

const onPrimaryClick = () => {
    if (!canSubmit.value) {
        return;
    }

    if (selected.value === GROUP_STATUS_CLOSED) {
        VxSwal.confirmation(
            'Close Group',
            'Closing this group will release all assigned phone numbers. Released numbers cannot be recovered by this group. This action cannot be undone.',
            'danger',
            () => submitImmediate()
        );
        return;
    }

    submitImmediate();
};

const submitImmediate = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.updateStatus(props.teamId, props.groupId, {
            status: selected.value,
        });
        await refresh();
        toasted.success('Group status updated successfully');
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const submitScheduled = async (scheduledDate: string) => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.scheduleStatusUpdate(props.teamId, props.groupId, {
            status: selected.value,
            scheduled_date: scheduledDate,
        });
        scheduleModalVisible.value = false;
        await refresh();
        toasted.success('Group status change scheduled');
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const cancelScheduled = () => {
    VxSwal.confirmation(
        'Cancel Scheduled Status Change',
        'This will remove the pending status change. You can schedule a new one afterwards.',
        'warning',
        () => submitCancelScheduled()
    );
};

const submitCancelScheduled = async () => {
    if (cancelling.value) {
        return;
    }

    cancelling.value = true;
    try {
        await adminGroupsClient.cancelScheduledStatusUpdate(props.teamId, props.groupId);
        await refresh();
        toasted.success('Scheduled status change cancelled');
    } catch (e) {
        catchError(e);
    } finally {
        cancelling.value = false;
    }
};
</script>
